import Tooltip from '@atlaskit/tooltip';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { type DropdownMenuProps } from '@databases/atlaskit/DropdownMenu';
import { messages } from '@databases/intl/messages';
import { logError } from '@databases/logs';
import { isMobile } from '@databases/modes';
import { defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import { SkeletonItem } from '@databases/skeleton';
import {
  DEFAULT_DATABASE_VIEW_SETTINGS,
  type DatabaseViewSettings,
} from '@databases/types/database/view';
import { ViewChangeTypes } from '@databases/utils/database/view/view';
import useView from '@databases/views/useView';
import React, { type FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { isApple } from '../../../utils';

import { MobileSearchWrapper } from './MobileQuickSearch';
import QuickSearch from './QuickSearch';

export interface DatabaseSearchProps {
  contentId?: string;
  value: DatabaseViewSettings;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  placement?: DropdownMenuProps['placement'];
  isFilterPickerOpen?: boolean;
  onFilterPickerOpenChange?: (isOpen: boolean) => void;
  showQuickSearch?: boolean;
}

interface TooltipProps {
  children: React.ReactNode;
}

/**
 * Renders different form fields to modify the settings of a database view.
 */
const DatabaseSearch: FunctionComponent<DatabaseSearchProps> = ({
  value = DEFAULT_DATABASE_VIEW_SETTINGS,
  showQuickSearch,
  contentId,
}) => {
  const client = useAnalytics();
  const { setViewChange } = useView();
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();

  const sendSearchedEvent = useCallback(
    async (search: DatabaseViewSettings['search']) => {
      if (search === '' || search === undefined) {
        // do not send event if search is empty
        return;
      }
      if (!contentId) {
        logError(
          new Error(
            'sendDatabasedSearchedEvent was not sent because contentId was not defined'
          )
        );
        return;
      }
      await client.sendDatabasedSearchedEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
      });
    },
    [client, contentId]
  );

  const handleSearchChange = useCallback(
    async (search: DatabaseViewSettings['search']) => {
      await sendSearchedEvent(search);
      setViewChange({
        type: ViewChangeTypes.VIEW_SEARCH,
        value: search,
      });
    },
    [setViewChange, sendSearchedEvent]
  );

  if (!showQuickSearch) {
    return null;
  }

  const IconTooltip: React.FC<TooltipProps> = ({ children }) => {
    return (
      <Tooltip
        hideTooltipOnClick
        delay={1000}
        content={
          <>
            {formatMessage(messages['quick-search.quick-search-tooltip-text'])}{' '}
            <kbd>
              {isApple
                ? formatMessage(messages['quick-search.mac-modifier'])
                : formatMessage(messages['quick-search.ctrl'])}
            </kbd>{' '}
            {formatMessage(messages['quick-search.plus-key'])}{' '}
            <kbd>{formatMessage(messages['quick-search.F-key'])}</kbd>
          </>
        }
      >
        {children}
      </Tooltip>
    );
  };

  return (
    <SkeletonItem isButton>
      {isMobile() ? (
        <MobileSearchWrapper
          tooltip={IconTooltip}
          value={value?.search || ''}
          onChange={handleSearchChange}
          onHandleExpandField={() => {
            dispatch(deselectAll());
          }}
        />
      ) : (
        <QuickSearch
          tooltip={IconTooltip}
          value={value?.search}
          onChange={handleSearchChange}
        />
      )}
    </SkeletonItem>
  );
};

export default DatabaseSearch;
